import React, { useCallback, useEffect, useState } from "react";
import api, { API_URL, formatRupiah, getToken, SPP_TAHUN } from "@/lib/api";
import Layout, { PageTitle } from "@/components/Layout";
import { NAV_BENDAHARA } from "@/lib/nav";
import { NAV_KEPSEK } from "@/lib/nav";
import { Download } from "lucide-react";
import { LineChart, Line, XAxis, YAxis, CartesianGrid, Tooltip, Legend, ResponsiveContainer } from "recharts";

const KOMPONEN = [
  { v: "pendaftaran", label: "Pendaftaran" },
  { v: "pangkal", label: "Pangkal" },
  { v: "spp", label: "SPP" },
  { v: "seragam", label: "Seragam" },
  { v: "rekap-bulanan", label: "Rekap Bulanan" },
  { v: "metode-pembayaran", label: "Metode Pembayaran" },
  { v: "grafik", label: "Grafik" },
];

const BULAN_SHORT = ["Jan", "Feb", "Mar", "Apr", "Mei", "Jun", "Jul", "Ags", "Sep", "Okt", "Nov", "Des"];

export function LaporanKeuangan({ nav = NAV_BENDAHARA }) {
  const [tahun, setTahun] = useState(SPP_TAHUN[0]);
  const [dataType, setDataType] = useState("pendaftaran");
  const [status, setStatus] = useState("aktif");
  const [applied, setApplied] = useState({ tahun: SPP_TAHUN[0], dataType: "pendaftaran", status: "aktif" });
  const [payload, setPayload] = useState(null); // {view, data}
  const [loading, setLoading] = useState(false);

  const KOMPONEN_KEYS = ["pendaftaran", "pangkal", "spp", "seragam"];
  const isKomponen = KOMPONEN_KEYS.includes(dataType);

  const load = useCallback(async () => {
    setLoading(true);
    setPayload(null);
    try {
      let d;
      const komp = KOMPONEN_KEYS.includes(applied.dataType);
      if (komp) {
        ({ data: d } = await api.get(`/laporan-keuangan/komponen/${applied.dataType}`, { params: { tahun: applied.tahun, status: applied.status } }));
      } else if (applied.dataType === "rekap-bulanan") {
        ({ data: d } = await api.get("/laporan-keuangan/rekap-bulanan", { params: { tahun: applied.tahun } }));
      } else if (applied.dataType === "metode-pembayaran") {
        ({ data: d } = await api.get("/laporan-keuangan/metode-pembayaran", { params: { tahun: applied.tahun } }));
      } else if (applied.dataType === "grafik") {
        ({ data: d } = await api.get("/laporan-keuangan/grafik", { params: { tahun: applied.tahun } }));
      }
      setPayload({ view: applied.dataType, data: d });
    } catch (e) { console.error(e); }
    finally { setLoading(false); }
  }, [applied]);

  useEffect(() => { load(); }, [load]);

  const applyFilter = () => setApplied({ tahun, dataType, status });
  const resetFilter = () => {
    setTahun(SPP_TAHUN[0]); setDataType("pendaftaran"); setStatus("aktif");
    setApplied({ tahun: SPP_TAHUN[0], dataType: "pendaftaran", status: "aktif" });
  };

  const downloadHref = () => {
    if (applied.dataType === "grafik") return null;
    const t = getToken();
    const params = new URLSearchParams({ tahun: applied.tahun });
    if (KOMPONEN_KEYS.includes(applied.dataType)) { params.append("view", "komponen"); params.append("komponen", applied.dataType); params.append("status", applied.status); }
    else params.append("view", applied.dataType);
    if (t) params.append("auth", t);
    return `${API_URL}/laporan-keuangan/download?${params.toString()}`;
  };

  const readyKomponen = payload && payload.view === applied.dataType && KOMPONEN_KEYS.includes(applied.dataType);
  const readyRekap = payload && payload.view === "rekap-bulanan";
  const readyMetode = payload && payload.view === "metode-pembayaran";
  const readyGrafik = payload && payload.view === "grafik";

  return (
    <Layout nav={nav}>
      <PageTitle title="Laporan Keuangan" action={
        downloadHref() ? (
          <a href={downloadHref()} target="_blank" rel="noreferrer" className="pp-btn-primary" data-testid="lk-download">
            <Download className="w-4 h-4" /> Download PDF
          </a>
        ) : null
      } />

      <section className="pp-card p-5 mb-6">
        <div className="grid sm:grid-cols-4 gap-3">
          <div>
            <label className="pp-label">Tahun</label>
            <select className="pp-input" value={tahun} onChange={(e) => setTahun(parseInt(e.target.value, 10))} data-testid="lk-tahun">
              {SPP_TAHUN.map((y) => <option key={y} value={y}>{y}</option>)}
            </select>
          </div>
          <div>
            <label className="pp-label">Data</label>
            <select className="pp-input" value={dataType} onChange={(e) => setDataType(e.target.value)} data-testid="lk-data">
              {KOMPONEN.map((k) => <option key={k.v} value={k.v}>{k.label}</option>)}
            </select>
          </div>
          {isKomponen && (
            <div>
              <label className="pp-label">Status</label>
              <select className="pp-input" value={status} onChange={(e) => setStatus(e.target.value)} data-testid="lk-status">
                <option value="aktif">Siswa Aktif</option>
                <option value="tidak_aktif">Siswa Tidak Aktif</option>
                <option value="semua">Seluruh Siswa</option>
              </select>
            </div>
          )}
          <div className="flex items-end gap-2">
            <button onClick={applyFilter} className="pp-btn-primary flex-1" data-testid="lk-apply">Terapkan</button>
            <button onClick={resetFilter} className="pp-btn-secondary flex-1" data-testid="lk-reset">Reset</button>
          </div>
        </div>
      </section>

      {loading && <div className="pp-card p-12 text-center text-slate-400 italic">Memuat…</div>}
      {!loading && readyKomponen && <KomponenTable data={payload.data} />}
      {!loading && readyRekap && <RekapBulananTable data={payload.data} />}
      {!loading && readyMetode && <MetodeTable data={payload.data} />}
      {!loading && readyGrafik && <GrafikView data={payload.data} />}
    </Layout>
  );
}

function KomponenTable({ data }) {
  return (
    <section className="pp-card pp-freeze overflow-hidden">
      <div className="overflow-x-auto">
        <table className="min-w-[1200px] w-full text-sm" data-testid="lk-komponen-table">
          <thead>
            <tr className="pp-table-header">
              <th className="text-left px-3 py-2 sticky left-0 bg-indigo-600 z-10 text-white">No</th>
              <th className="text-left px-3 py-2 sticky left-10 bg-indigo-600 z-10 min-w-[200px] text-white">Nama</th>
              {BULAN_SHORT.map((b) => <th key={b} className="text-right px-2 py-2">{b}</th>)}
              <th className="text-right px-3 py-2">TOTAL</th>
            </tr>
          </thead>
          <tbody>
            {data.rows.length === 0 && <tr><td colSpan={15} className="text-center py-10 text-slate-400 italic">Belum ada data.</td></tr>}
            {data.rows.map((row, idx) => (
              <tr key={row.siswa_id} className="pp-row" data-testid={`lk-row-${row.siswa_id}`}>
                <td className="px-3 py-2 sticky left-0 bg-white z-10 text-slate-500">{idx + 1}</td>
                <td className="px-3 py-2 sticky left-10 bg-white z-10 font-medium">{row.nama}</td>
                {["01","02","03","04","05","06","07","08","09","10","11","12"].map((b) => {
                  const v = row.per_bulan[b];
                  const isTunggak = row.tunggakan?.[b];
                  return (
                    <td key={b} className={`px-2 py-2 text-right font-mono text-xs ${isTunggak ? "bg-amber-100" : ""}`}>
                      {v > 0 ? formatRupiah(v) : "—"}
                    </td>
                  );
                })}
                <td className="px-3 py-2 text-right font-mono font-semibold">{formatRupiah(row.total)}</td>
              </tr>
            ))}
          </tbody>
          <tfoot>
            <tr className="bg-indigo-50 font-bold">
              <td className="px-3 py-2 sticky left-0 bg-indigo-50 z-10"></td>
              <td className="px-3 py-2 sticky left-10 bg-indigo-50 z-10">TOTAL</td>
              {["01","02","03","04","05","06","07","08","09","10","11","12"].map((b) => (
                <td key={b} className="px-2 py-2 text-right font-mono text-xs">{data.col_totals[b] > 0 ? formatRupiah(data.col_totals[b]) : "—"}</td>
              ))}
              <td className="px-3 py-2 text-right font-mono">{formatRupiah(data.grand_total)}</td>
            </tr>
          </tfoot>
        </table>
      </div>
    </section>
  );
}

function RekapBulananTable({ data }) {
  return (
    <section className="pp-card pp-freeze overflow-hidden">
      <table className="w-full text-sm" data-testid="lk-rekap-table">
        <thead><tr className="pp-table-header">
          <th className="text-left px-4 py-3">Bulan</th>
          <th className="text-right px-4 py-3">Pendaftaran</th>
          <th className="text-right px-4 py-3">Pangkal</th>
          <th className="text-right px-4 py-3">SPP</th>
          <th className="text-right px-4 py-3">Seragam</th>
          <th className="text-right px-4 py-3">TOTAL</th>
        </tr></thead>
        <tbody>
          {data.rows.map((r) => (
            <tr key={r.bulan} className="pp-row">
              <td className="px-4 py-2 font-medium">{r.label}</td>
              <td className="px-4 py-2 text-right font-mono text-xs">{r.pendaftaran ? formatRupiah(r.pendaftaran) : "—"}</td>
              <td className="px-4 py-2 text-right font-mono text-xs">{r.pangkal ? formatRupiah(r.pangkal) : "—"}</td>
              <td className="px-4 py-2 text-right font-mono text-xs">{r.spp ? formatRupiah(r.spp) : "—"}</td>
              <td className="px-4 py-2 text-right font-mono text-xs">{r.seragam ? formatRupiah(r.seragam) : "—"}</td>
              <td className="px-4 py-2 text-right font-mono font-semibold">{r.total ? formatRupiah(r.total) : "—"}</td>
            </tr>
          ))}
        </tbody>
        <tfoot>
          <tr className="bg-indigo-50 font-bold">
            <td className="px-4 py-3">TOTAL</td>
            <td className="px-4 py-3 text-right font-mono">{formatRupiah(data.total.pendaftaran)}</td>
            <td className="px-4 py-3 text-right font-mono">{formatRupiah(data.total.pangkal)}</td>
            <td className="px-4 py-3 text-right font-mono">{formatRupiah(data.total.spp)}</td>
            <td className="px-4 py-3 text-right font-mono">{formatRupiah(data.total.seragam)}</td>
            <td className="px-4 py-3 text-right font-mono">{formatRupiah(data.total.total)}</td>
          </tr>
        </tfoot>
      </table>
    </section>
  );
}

function MetodeTable({ data }) {
  return (
    <section className="pp-card pp-freeze overflow-hidden">
      <table className="w-full text-sm" data-testid="lk-metode-table">
        <thead><tr className="pp-table-header">
          <th className="text-left px-4 py-3">Bulan</th>
          <th className="text-right px-4 py-3">Tunai</th>
          <th className="text-right px-4 py-3">Transfer</th>
          <th className="text-right px-4 py-3">TOTAL</th>
        </tr></thead>
        <tbody>
          {data.rows.map((r) => (
            <tr key={r.bulan} className="pp-row">
              <td className="px-4 py-2 font-medium">{r.label}</td>
              <td className="px-4 py-2 text-right font-mono">{r.tunai ? formatRupiah(r.tunai) : "—"}</td>
              <td className="px-4 py-2 text-right font-mono">{r.transfer ? formatRupiah(r.transfer) : "—"}</td>
              <td className="px-4 py-2 text-right font-mono font-semibold">{r.total ? formatRupiah(r.total) : "—"}</td>
            </tr>
          ))}
        </tbody>
        <tfoot>
          <tr className="bg-indigo-50 font-bold">
            <td className="px-4 py-3">TOTAL</td>
            <td className="px-4 py-3 text-right font-mono">{formatRupiah(data.total.tunai)}</td>
            <td className="px-4 py-3 text-right font-mono">{formatRupiah(data.total.transfer)}</td>
            <td className="px-4 py-3 text-right font-mono">{formatRupiah(data.total.total)}</td>
          </tr>
        </tfoot>
      </table>
    </section>
  );
}

function GrafikView({ data }) {
  return (
    <section className="pp-card p-5" data-testid="lk-chart">
      <h3 className="text-lg font-semibold text-slate-900 mb-4">Grafik Penerimaan Dana {data.tahun}</h3>
      <div className="h-96">
        <ResponsiveContainer width="100%" height="100%">
          <LineChart data={data.series} margin={{ top: 12, right: 24, left: 8, bottom: 8 }}>
            <CartesianGrid strokeDasharray="3 3" stroke="#E2E8F0" />
            <XAxis dataKey="bulan" tick={{ fontSize: 11 }} />
            <YAxis tickFormatter={(v) => `${(v / 1000000).toFixed(0)}Jt`} tick={{ fontSize: 11 }} />
            <Tooltip formatter={(v) => formatRupiah(v)} />
            <Legend />
            <Line type="monotone" dataKey="pendaftaran" name="Pendaftaran" stroke="#4F46E5" strokeWidth={2} />
            <Line type="monotone" dataKey="pangkal" name="Pangkal" stroke="#EA580C" strokeWidth={2} />
            <Line type="monotone" dataKey="spp" name="SPP" stroke="#059669" strokeWidth={2} />
            <Line type="monotone" dataKey="seragam" name="Seragam" stroke="#DB2777" strokeWidth={2} />
          </LineChart>
        </ResponsiveContainer>
      </div>
    </section>
  );
}

export function LaporanKeuanganKepsek() {
  return <LaporanKeuangan nav={NAV_KEPSEK} />;
}
